import { API_PREFIX, MAX_PAGE_SIZE } from '@hit-game/shared/constants';

import api, { type ApiRequestConfig } from './api';

/**
 * Envelope THÀNH CÔNG của server: `{ code, message, data }`.
 *
 * Lỗi KHÔNG dùng shape này — lỗi theo RFC 9457 (`application/problem+json`),
 * bóc ở `api.ts`. Ở đây chỉ có đường 2xx nên không cần field phân biệt.
 */
interface Envelope<T> {
  code: number;
  message: string;
  data: T;
}

/**
 * Bóc `data` khỏi envelope, một lần cho toàn bộ app.
 *
 * Trước đây mỗi service tự `try/catch` + kiểm tra `code === 200` + `console.error`
 * + trả `[]`/`null` khi lỗi — lặp ~40 lần và nuốt mất lỗi, khiến màn hình hiện
 * "không có dữ liệu" trong khi thực chất request đã fail. Ở đây lỗi được NÉM ra
 * để caller (hoặc hook) quyết định hiển thị, còn toast thì interceptor đã lo.
 */
export const http = {
  get<T>(url: string, config?: ApiRequestConfig): Promise<T> {
    return api.get<Envelope<T>>(`${API_PREFIX}${url}`, config).then((r) => r.data.data);
  },
  post<T>(url: string, body?: unknown, config?: ApiRequestConfig): Promise<T> {
    return api.post<Envelope<T>>(`${API_PREFIX}${url}`, body, config).then((r) => r.data.data);
  },
  put<T>(url: string, body?: unknown, config?: ApiRequestConfig): Promise<T> {
    return api.put<Envelope<T>>(`${API_PREFIX}${url}`, body, config).then((r) => r.data.data);
  },
  patch<T>(url: string, body?: unknown, config?: ApiRequestConfig): Promise<T> {
    return api.patch<Envelope<T>>(`${API_PREFIX}${url}`, body, config).then((r) => r.data.data);
  },
  delete<T = null>(url: string, config?: ApiRequestConfig): Promise<T> {
    return api.delete<Envelope<T>>(`${API_PREFIX}${url}`, config).then((r) => r.data.data);
  },
};

/** Trang dữ liệu server trả về. */
export interface Paginated<T> {
  items: T[];
  total: number;
  totalPages: number;
}

/**
 * Trần số trang kéo về trong một lần `fetchAllPages`.
 *
 * `MAX_PAGE_SIZE` (100) × 50 = 5.000 bản ghi — quá con số đó thì màn hình chọn
 * không còn dùng được bằng cách cuộn nữa, vấn đề là ở giao diện chứ không phải
 * ở đây. Trần này để một `totalPages` sai từ server không kéo vòng lặp chạy mãi.
 */
const MAX_FETCH_ALL_PAGES = 50;

/**
 * Kéo TOÀN BỘ bản ghi của một endpoint phân trang.
 *
 * Dùng cho các bộ chọn (dropdown proxy, dropdown tài khoản, danh sách nhóm ngoài
 * sảnh) — những chỗ cần đủ danh sách chứ không phải một trang.
 *
 * Không xin `limit` thật to: `processPagination` bên server KẸP limit về
 * `MAX_PAGE_SIZE`, nên `limit: 500` chỉ nhận về 100 dòng — không lỗi, không log,
 * và bản ghi thứ 101 trở đi biến mất khỏi bộ chọn. Ở đây xin đúng trần rồi lặp
 * cho tới hết.
 */
export async function fetchAllPages<T>(
  fetchPage: (params: { page: number; limit: number }) => Promise<Paginated<T>>,
): Promise<T[]> {
  const items: T[] = [];

  for (let page = 1; page <= MAX_FETCH_ALL_PAGES; page += 1) {
    const result = await fetchPage({ page, limit: MAX_PAGE_SIZE });
    items.push(...result.items);

    // Dừng theo SỐ DÒNG nhận được, không chỉ theo `totalPages`: trang thiếu dòng
    // là trang cuối, kể cả khi server báo còn trang (dữ liệu vừa bị xoá bớt).
    if (result.items.length < MAX_PAGE_SIZE || page >= result.totalPages) break;
  }

  return items;
}
